import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  Field,
  Input,
  Label,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
  Textarea,
} from "@a7y/tailwind-catalyst";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Megaphone } from "lucide-react";
import { useState, type ChangeEvent } from "react";
import { formatCents, LoadingPanel } from "./shared";
import type { AffiliateCampaign } from "./types";

const CURRENCIES = ["usd", "eur", "gbp", "cad", "aud"];

type StatusMessage = { type: "success" | "error"; text: string };

type FormState = {
  commissionPercent: string;
  commissionDurationMonths: string;
  cookieWindowDays: string;
  holdPeriodDays: string;
  minimumPayoutCents: string;
  minimumPayoutCurrency: string;
  isPrivate: boolean;
  portalWelcomeText: string;
};

function emptyForm(): FormState {
  return {
    commissionPercent: "30",
    commissionDurationMonths: "12",
    cookieWindowDays: "60",
    holdPeriodDays: "30",
    minimumPayoutCents: "5000",
    minimumPayoutCurrency: "usd",
    isPrivate: false,
    portalWelcomeText: "",
  };
}

function formFromCampaign(c: AffiliateCampaign): FormState {
  return {
    commissionPercent: String(c.commissionPercent),
    commissionDurationMonths: String(c.commissionDurationMonths),
    cookieWindowDays: String(c.cookieWindowDays),
    holdPeriodDays: String(c.holdPeriodDays),
    minimumPayoutCents: String(c.minimumPayoutCents),
    minimumPayoutCurrency: c.minimumPayoutCurrency,
    isPrivate: c.isPrivate,
    portalWelcomeText: c.portalWelcomeText ?? "",
  };
}

export function CampaignsPage() {
  const queryClient = useQueryClient();
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);
  const [editing, setEditing] = useState<AffiliateCampaign | null>(null);
  const [creating, setCreating] = useState(false);

  const showStatus = (type: "success" | "error", text: string) => {
    setStatusMessage({ type, text });
    setTimeout(() => setStatusMessage(null), 4000);
  };

  const { data, isLoading } = useQuery({
    queryKey: ["affiliate-campaigns"],
    queryFn: () => api.affiliates.listCampaigns(),
  });

  const campaigns: AffiliateCampaign[] = data?.campaigns ?? [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["affiliate-campaigns"] });

  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => api.affiliates.setDefaultCampaign(id),
    onSuccess: () => {
      invalidate();
      showStatus("success", "Default campaign updated");
    },
    onError: (err: Error) => showStatus("error", `Failed: ${err.message}`),
  });

  return (
    <div className="px-4 py-0">
      <PageHeader title="Affiliate Campaigns" subtitle="Commission & policy templates">
        <Button color="blue" onClick={() => setCreating(true)}>
          New campaign
        </Button>
      </PageHeader>

      {statusMessage && (
        <div
          className={`mb-4 rounded-lg px-4 py-2.5 text-sm font-medium ${
            statusMessage.type === "success"
              ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {statusMessage.text}
        </div>
      )}

      {isLoading ? (
        <LoadingPanel />
      ) : campaigns.length === 0 ? (
        <div className="py-16 text-center">
          <Megaphone className="mx-auto h-12 w-12 text-zinc-400" />
          <Text className="mt-2 text-zinc-500">No campaigns yet</Text>
        </div>
      ) : (
        <Table dense striped>
          <TableHead>
            <TableRow>
              <TableHeader>Campaign</TableHeader>
              <TableHeader>Commission</TableHeader>
              <TableHeader>Duration</TableHeader>
              <TableHeader>Cookie</TableHeader>
              <TableHeader>Hold</TableHeader>
              <TableHeader>Min payout</TableHeader>
              <TableHeader>Visibility</TableHeader>
              <TableHeader className="text-right">Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.map((c) => (
              <TableRow key={c.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs">{c.id}</span>
                    {c.isDefault && <Badge color="green">DEFAULT</Badge>}
                  </div>
                </TableCell>
                <TableCell>{c.commissionPercent}%</TableCell>
                <TableCell>{c.commissionDurationMonths}mo</TableCell>
                <TableCell>{c.cookieWindowDays}d</TableCell>
                <TableCell>{c.holdPeriodDays}d</TableCell>
                <TableCell>{formatCents(c.minimumPayoutCents, c.minimumPayoutCurrency)}</TableCell>
                <TableCell>
                  {c.isPrivate ? <Badge color="amber">Private</Badge> : <Badge color="zinc">Public</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button plain onClick={() => setEditing(c)}>
                      Edit
                    </Button>
                    {!c.isDefault && (
                      <Button
                        outline
                        disabled={setDefaultMutation.isPending}
                        onClick={() => setDefaultMutation.mutate(c.id)}
                      >
                        Set default
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {(creating || editing) && (
        <CampaignFormDialog
          campaign={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            invalidate();
            setCreating(false);
            setEditing(null);
            showStatus("success", editing ? "Campaign updated" : "Campaign created");
          }}
          onError={(msg) => showStatus("error", msg)}
        />
      )}
    </div>
  );
}

function CampaignFormDialog({
  campaign,
  onClose,
  onSaved,
  onError,
}: {
  campaign: AffiliateCampaign | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}) {
  const [form, setForm] = useState<FormState>(
    campaign ? formFromCampaign(campaign) : emptyForm(),
  );

  const mutation = useMutation({
    mutationFn: () => {
      const body: Partial<AffiliateCampaign> = {
        commissionPercent: Number(form.commissionPercent),
        commissionDurationMonths: Number(form.commissionDurationMonths),
        cookieWindowDays: Number(form.cookieWindowDays),
        holdPeriodDays: Number(form.holdPeriodDays),
        minimumPayoutCents: Number(form.minimumPayoutCents),
        minimumPayoutCurrency: form.minimumPayoutCurrency as AffiliateCampaign["minimumPayoutCurrency"],
        isPrivate: form.isPrivate,
        portalWelcomeText: form.portalWelcomeText.trim() === "" ? null : form.portalWelcomeText.trim(),
      };
      return campaign
        ? api.affiliates.updateCampaign(campaign.id, body)
        : api.affiliates.createCampaign(body);
    },
    onSuccess: onSaved,
    onError: (err: Error) => onError(`Save failed: ${err.message}`),
  });

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const numberField = (
    label: string,
    key: keyof Pick<
      FormState,
      | "commissionPercent"
      | "commissionDurationMonths"
      | "cookieWindowDays"
      | "holdPeriodDays"
      | "minimumPayoutCents"
    >,
    hint?: string,
  ) => (
    <Field>
      <Label>{label}</Label>
      <Input
        type="number"
        value={form[key]}
        onChange={(e: ChangeEvent<HTMLInputElement>) => set(key, e.target.value)}
      />
      {hint && <Text className="mt-1 text-xs text-zinc-400">{hint}</Text>}
    </Field>
  );

  return (
    <Dialog open onClose={onClose} size="2xl">
      <DialogTitle>{campaign ? "Edit campaign" : "New campaign"}</DialogTitle>
      <DialogBody>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {numberField("Commission %", "commissionPercent")}
          {numberField("Commission duration (months)", "commissionDurationMonths")}
          {numberField("Cookie window (days)", "cookieWindowDays")}
          {numberField("Hold period (days)", "holdPeriodDays")}
          {numberField("Minimum payout (cents)", "minimumPayoutCents", "Minor units, e.g. 5000 = $50.00")}
          <Field>
            <Label>Minimum payout currency</Label>
            <Select
              value={form.minimumPayoutCurrency}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                set("minimumPayoutCurrency", e.target.value)
              }
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c.toUpperCase()}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="mt-4 flex items-center justify-between rounded-lg bg-zinc-50 px-4 py-3 dark:bg-zinc-800/50">
          <div>
            <Text className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
              Private campaign
            </Text>
            <Text className="text-xs text-zinc-500">
              Hidden from public signup; affiliates must be invited.
            </Text>
          </div>
          <Switch checked={form.isPrivate} onChange={(v: boolean) => set("isPrivate", v)} />
        </div>

        <Field className="mt-4">
          <Label>Portal welcome text</Label>
          <Textarea
            value={form.portalWelcomeText}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => set("portalWelcomeText", e.target.value)}
            rows={3}
            placeholder="Optional welcome message shown to affiliates on this campaign."
          />
        </Field>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button color="blue" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? "Saving..." : campaign ? "Save changes" : "Create campaign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
